/**
 * AnnouncementBar — dual Swiper (desktop pairs / mobile singles).
 * Requires window.Swiper from swiper-bundle.min.js
 */
if (!window.AnnouncementBarInitialized) {
  window.AnnouncementBarInitialized = true;

  class AnnouncementBar {
    static instances = new Map();

    /**
     * @param {HTMLElement} section
     */
    constructor(section) {
      if (!(section instanceof HTMLElement)) return;

      const existing = AnnouncementBar.instances.get(section.id);
      if (existing) existing.destroy();

      this.section = section;
      this.desktopEl = section.querySelector('[data-announcement-swiper="desktop"]');
      this.mobileEl = section.querySelector('[data-announcement-swiper="mobile"]');
      this.desktopSwiper = null;
      this.mobileSwiper = null;

      this.settings = {
        autoplay: section.dataset.autoplay === 'true',
        autoplaySpeed: Number(section.dataset.autoplaySpeed) || 5,
        speed: Number(section.dataset.speed) || 400,
        pauseOnHover: section.dataset.pauseOnHover !== 'false',
      };

      this._readyPromise = this._whenSwiperReady().then(() => this.init());
      AnnouncementBar.instances.set(section.id, this);
    }

    get autoplayConfig() {
      if (!this.settings.autoplay) return false;

      return {
        delay: this.settings.autoplaySpeed * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: this.settings.pauseOnHover,
      };
    }

    /**
     * Build Swiper navigation config from sibling prev/next buttons in the slider wrapper.
     * @param {HTMLElement} swiperEl
     * @returns {false | { prevEl: Element, nextEl: Element }}
     */
    _navigationConfig(swiperEl) {
      const wrapper = swiperEl.parentElement;
      if (!wrapper) return false;

      const prevEl = wrapper.querySelector('[data-announcement-prev]');
      const nextEl = wrapper.querySelector('[data-announcement-next]');

      if (!prevEl || !nextEl) return false;

      return { prevEl, nextEl };
    }

    /**
     * Swiper is deferred at end of body; section script may run first.
     * @returns {Promise<void>}
     */
    _whenSwiperReady() {
      if (typeof window.Swiper !== 'undefined') {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const started = Date.now();
        const timer = window.setInterval(() => {
          if (typeof window.Swiper !== 'undefined') {
            window.clearInterval(timer);
            resolve();
          } else if (Date.now() - started > 8000) {
            window.clearInterval(timer);
            reject(new Error('[AnnouncementBar] Swiper failed to load'));
          }
        }, 50);
      });
    }

    init() {
      if (typeof window.Swiper === 'undefined') return;

      if (this.desktopEl) {
        const slideCount = this.desktopEl.querySelectorAll('.swiper-slide').length;
        const navigation = this._navigationConfig(this.desktopEl);

        this.desktopSwiper = new window.Swiper(this.desktopEl, {
          slidesPerView: 1,
          loop: slideCount > 1,
          speed: this.settings.speed,
          autoplay: this.autoplayConfig,
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          allowTouchMove: true,
          watchOverflow: true,
          ...(navigation ? { navigation } : {}),
        });
      }

      if (this.mobileEl) {
        const slideCount = this.mobileEl.querySelectorAll('.swiper-slide').length;
        const navigation = this._navigationConfig(this.mobileEl);

        this.mobileSwiper = new window.Swiper(this.mobileEl, {
          slidesPerView: 1,
          loop: slideCount > 1,
          speed: this.settings.speed,
          autoplay: this.autoplayConfig,
          allowTouchMove: true,
          watchOverflow: true,
          ...(navigation ? { navigation } : {}),
        });
      }
    }

    destroy() {
      if (this.desktopSwiper) {
        this.desktopSwiper.destroy(true, true);
        this.desktopSwiper = null;
      }

      if (this.mobileSwiper) {
        this.mobileSwiper.destroy(true, true);
        this.mobileSwiper = null;
      }

      AnnouncementBar.instances.delete(this.section?.id);
    }

    /**
     * @param {HTMLElement | null} sectionEl
     */
    static initSection(sectionEl) {
      if (!sectionEl) return;
      new AnnouncementBar(sectionEl);
    }

    /**
     * @param {HTMLElement | null} sectionEl
     */
    static destroySection(sectionEl) {
      if (!sectionEl) return;
      const instance = AnnouncementBar.instances.get(sectionEl.id);
      if (instance) instance.destroy();
    }

    /**
     * Resolve the announcement root from a Theme Editor event target.
     * @param {EventTarget | null} target
     * @returns {HTMLElement | null}
     */
    static findRoot(target) {
      if (!(target instanceof HTMLElement)) return null;

      if (target.matches('[data-section-type="announcement-bar-new"]')) {
        return target;
      }

      return target.querySelector('[data-section-type="announcement-bar-new"]');
    }
  }

  function bootAnnouncementBars() {
    document.querySelectorAll('[data-section-type="announcement-bar-new"]').forEach((el) => {
      AnnouncementBar.initSection(el);
    });
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = AnnouncementBar.findRoot(event.target);
    if (section) AnnouncementBar.initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = AnnouncementBar.findRoot(event.target);
    if (section) AnnouncementBar.destroySection(section);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAnnouncementBars);
  } else {
    bootAnnouncementBars();
  }

  window.AnnouncementBar = AnnouncementBar;
}
